import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function NPVCalculator() {
  const [initialInvestment, setInitialInvestment] = useState("");
  const [discountRate, setDiscountRate] = useState("");
  const [cashFlows, setCashFlows] = useState("");
  const [result, setResult] = useState<{ npv: number; interpretation: string } | null>(null);

  const calculate = () => {
    const investment = parseFloat(initialInvestment);
    const rate = parseFloat(discountRate) / 100;
    const flows = cashFlows.split(",").map(cf => parseFloat(cf.trim()));

    if (isNaN(investment) || isNaN(rate) || flows.some(isNaN) || flows.length === 0) {
      toast.error("Please enter valid values");
      return;
    }

    // NPV = -Initial Investment + Σ(Cash Flow / (1 + r)^t)
    let npv = -investment;
    flows.forEach((flow, index) => {
      npv += flow / Math.pow(1 + rate, index + 1);
    });

    const interpretation = npv > 0 
      ? "Accept - Project adds value" 
      : npv < 0 
      ? "Reject - Project destroys value" 
      : "Neutral - Break-even project";

    setResult({ npv, interpretation });
    toast.success("NPV calculated!");
  };

  const reset = () => {
    setInitialInvestment("");
    setDiscountRate("");
    setCashFlows("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Net Present Value Calculator</CardTitle>
          <CardDescription>
            Calculate Net Present Value of future cash flows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="investment">Initial Investment ($)</Label>
            <Input
              id="investment"
              type="number"
              placeholder="100000"
              value={initialInvestment}
              onChange={(e) => setInitialInvestment(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Discount Rate (%)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="10"
              value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="flows">Cash Flows (comma-separated by year)</Label>
            <Input
              id="flows"
              type="text"
              placeholder="30000, 40000, 50000, 60000"
              value={cashFlows}
              onChange={(e) => setCashFlows(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>NPV:</strong> ${result.npv.toFixed(2)}
                </p>
                <CopyResultButton value={result.npv.toFixed(2)} />
              </div>
              <p className="text-sm">
                <strong>Decision:</strong> {result.interpretation}
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["npv"]} trigger={result?.npv} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
